// CHG-132 REQ-PA-006~009: 프로젝트 보관 다이얼로그
import { useState, useEffect } from 'react';
import { X, Archive, FileText, Trash2, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import Spinner from '../ui/Spinner';
import { useArchiveProject } from '../../hooks/useProjects';
import type { ArchiveMode } from '../../services/projectService';

interface ArchiveProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  projectName: string;
  projectKey: string;
  onArchived: (mode: ArchiveMode) => void;
}

const MODE_OPTIONS: { value: ArchiveMode; label: string; description: string; icon: React.ReactNode }[] = [
  {
    value: 'GRADUATE',
    label: '졸업 — 팀 문서함으로 변환',
    description: '프로젝트 문서함을 팀 문서함으로 전환합니다. 모든 문서가 보존되며, 같은 조직 멤버가 검색·열람할 수 있습니다.',
    icon: <FileText className="size-4 text-blue-500" />,
  },
  {
    value: 'KEEP',
    label: '유지 — 문서함 그대로 보관',
    description: '문서함을 현재 형태로 유지합니다. 멤버 외에는 검색·접근할 수 없습니다.',
    icon: <Archive className="size-4 text-gray-500" />,
  },
  {
    value: 'DELETE',
    label: '삭제 — 문서함 완전 제거',
    description: '문서함과 모든 문서를 완전히 삭제합니다. 이 작업은 되돌릴 수 없습니다.',
    icon: <Trash2 className="size-4 text-red-500" />,
  },
];

export default function ArchiveProjectModal({
  isOpen,
  onClose,
  projectId,
  projectName,
  projectKey,
  onArchived,
}: ArchiveProjectModalProps) {
  const [mode, setMode] = useState<ArchiveMode>('GRADUATE');
  const [graduateSpaceName, setGraduateSpaceName] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const archiveMutation = useArchiveProject();

  // 모달 열릴 때 초기화
  useEffect(() => {
    if (isOpen) {
      setMode('GRADUATE');
      setGraduateSpaceName(`${projectName} 아카이브`);
      setDeleteConfirmText('');
    }
  }, [isOpen, projectName]);

  // ESC로 닫기
  useEffect(() => {
    if (!isOpen) return;
    const handle = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !archiveMutation.isPending) onClose();
    };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [isOpen, onClose, archiveMutation.isPending]);

  if (!isOpen) return null;

  const isDeleteReady = mode !== 'DELETE' || deleteConfirmText === projectKey;
  const canSubmit = isDeleteReady && !archiveMutation.isPending;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      await archiveMutation.mutateAsync({
        id: projectId,
        mode,
        graduateSpaceName: mode === 'GRADUATE' ? graduateSpaceName.trim() || undefined : undefined,
      });
      toast.success(
        mode === 'GRADUATE'
          ? '프로젝트가 보관되고 팀 문서함으로 졸업되었습니다.'
          : mode === 'DELETE'
          ? '프로젝트가 보관되고 문서함이 삭제되었습니다.'
          : '프로젝트가 보관되었습니다.',
      );
      onArchived(mode);
    } catch (err) {
      const error = err as { response?: { data?: { error?: string; message?: string } } };
      const msg = error.response?.data?.message || error.response?.data?.error || '보관에 실패했습니다.';
      toast.error(msg);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="archive-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={() => !archiveMutation.isPending && onClose()}
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-2">
            <Archive className="size-5 text-amber-600" />
            <h3 id="archive-modal-title" className="text-lg font-semibold text-gray-900">
              프로젝트 보관
            </h3>
          </div>
          <button
            onClick={onClose}
            disabled={archiveMutation.isPending}
            className="p-1 text-gray-400 hover:text-gray-600 rounded disabled:opacity-50"
            aria-label="닫기"
          >
            <X className="size-5" />
          </button>
        </div>

        {/* 본문 */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* 프로젝트 정보 + 안내 */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex gap-2">
            <AlertTriangle className="size-4 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900">
              <p className="font-medium">
                <span className="font-semibold">{projectName}</span> 프로젝트를 보관합니다.
              </p>
              <p className="mt-1">보관된 프로젝트는 읽기 전용으로 전환되며, 보관함에서 언제든 확인할 수 있습니다.</p>
            </div>
          </div>

          {/* 모드 선택 */}
          <div className="space-y-2">
            <label className="text-sm font-semibold text-gray-900">문서함 처리 방식</label>
            <div className="space-y-2">
              {MODE_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className={`flex gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    mode === opt.value
                      ? 'border-primary-500 bg-primary-50/50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="archive-mode"
                    value={opt.value}
                    checked={mode === opt.value}
                    onChange={() => setMode(opt.value)}
                    className="mt-0.5 text-primary-600 focus:ring-primary-500"
                    disabled={archiveMutation.isPending}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 text-sm font-medium text-gray-900">
                      {opt.icon}
                      {opt.label}
                    </div>
                    <p className="text-sm text-gray-500 mt-1">{opt.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* GRADUATE: 팀 문서함 이름 입력 */}
          {mode === 'GRADUATE' && (
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-900">팀 문서함 이름</label>
              <input
                value={graduateSpaceName}
                onChange={(e) => setGraduateSpaceName(e.target.value)}
                maxLength={100}
                placeholder={`${projectName} 아카이브`}
                disabled={archiveMutation.isPending}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-primary-500 outline-none disabled:bg-gray-50"
              />
              <p className="text-sm text-gray-500">비워두면 기본값(&apos;{projectName} 아카이브&apos;)으로 저장됩니다.</p>
            </div>
          )}

          {/* DELETE: 재확인 입력 */}
          {mode === 'DELETE' && (
            <div className="space-y-1.5 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">
                문서함과 모든 문서를 영구적으로 삭제합니다. 확인을 위해 프로젝트 키{' '}
                <strong>{projectKey}</strong>를 입력하세요.
              </p>
              <input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder={projectKey}
                disabled={archiveMutation.isPending}
                className="w-full px-3 py-2 border border-red-300 rounded-lg text-sm focus:ring-1 focus:ring-red-500 outline-none disabled:bg-gray-50"
              />
            </div>
          )}
        </div>

        {/* 푸터 */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100 shrink-0">
          <button
            onClick={onClose}
            disabled={archiveMutation.isPending}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            취소
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${
              mode === 'DELETE'
                ? 'bg-red-600 text-white hover:bg-red-700'
                : 'bg-amber-600 text-white hover:bg-amber-700'
            }`}
          >
            {archiveMutation.isPending && <Spinner className="size-4 text-white" />}
            {mode === 'DELETE' ? '보관 + 문서함 삭제' : '보관'}
          </button>
        </div>
      </div>
    </div>
  );
}
